import { useEffect, useRef, useState } from 'react';
import { API_V1 } from '@utils/apiBase';

/**
 * Slide thumbnails need the Authorization header, so an <img src> cannot fetch
 * them: each is loaded as a Blob and handed to the rail as an object URL.
 *
 * Loaded one at a time — a long deck must not open one connection per slide, and
 * the rail fills progressively. The array is always slideCount long, so position N
 * is slide N + 1; a slide that fails stays null and the rest of the deck still
 * loads, rather than one transient 404 hiding every slide after it.
 */
export function useSlideThumbnails(
  documentId: string | undefined,
  slideCount: number,
): (string | null)[] {
  const [thumbnails, setThumbnails] = useState<(string | null)[]>([]);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    const revoke = () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };

    if (!documentId || slideCount < 1) {
      setThumbnails([]);
      return undefined;
    }

    setThumbnails(new Array(slideCount).fill(null));

    const abortController = new AbortController();
    const token = sessionStorage.getItem('access_token');

    const place = (index: number, url: string) => {
      setThumbnails((current) => {
        const next = [...current];
        next[index] = url;
        return next;
      });
    };

    const load = async () => {
      for (let slide = 1; slide <= slideCount; slide += 1) {
        try {
          const response = await fetch(`${API_V1}/documents/${documentId}/slides/${slide}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: abortController.signal,
          });
          if (abortController.signal.aborted) return;
          if (!response.ok) continue;

          const url = URL.createObjectURL(await response.blob());
          if (abortController.signal.aborted) {
            URL.revokeObjectURL(url);
            return;
          }
          urlsRef.current.push(url);
          place(slide - 1, url);
        } catch {
          if (abortController.signal.aborted) return;
        }
      }
    };

    void load();

    return () => {
      abortController.abort();
      revoke();
    };
  }, [documentId, slideCount]);

  return thumbnails;
}
